import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Dialog, DialogContent } from "@mui/material";
import { DataTable } from "../../../../Component/Common/Components/DataTable/DataTable";
import { DeviationsService } from "../../../../Component/Api/EmploymentEquity/Deviations/DeviationsService";
import { DeviationsDto } from "../../../../Component/Model/Dto/EmploymentEquity/Deviations";
import { SearchResult } from "../../../../Component/Model/Dto/SearchResult";
import { ColumnSortOption } from "../../../../Component/Model/Dto/ColumnSortOption";
import { EntriesPerPage } from "../../../../Component/Model/Table/Table";
import { PlanSummaryMovementType } from "../../../../Component/Model/Enum/EmploymentEquity";
import { CustomDialogTitle } from "../../../Common/Custom/CustomDialogTitle";
import { PlanSummaryMovementIndividualTableMapper } from "./PlanSummaryMovementIndividualTableColumn";

interface IProps {
  showModal: boolean;
  handleCancel: () => void;
  title: string;
  scorecardId: string;
  occupationalLevelId: number;
  raceId: number | null;
  genderId: number | null;
  movementType: PlanSummaryMovementType;
}

export const PlanSummaryMovementIndividualTable = (props: IProps) => {
  const [data, setData] = useState<SearchResult<DeviationsDto.PlanSummaryMovementIndividualDto> | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [searchValue, setSearchValue] = useState<string>("");
  const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({ defaultValue: 10 });
  const [columnSortOptions, setColumnSortOptions] = useState<ColumnSortOption[]>([]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const request: DeviationsDto.PlanSummaryMovementIndividualsRequest = {
        pageNumber,
        pageSize: entriesPerPage.defaultValue!,
        searchValue,
        columnSortOptions,
        scorecardId: props.scorecardId,
        occupationalLevelId: props.occupationalLevelId,
        raceId: props.raceId,
        genderId: props.genderId,
        movementType: props.movementType,
      };
      const response = await DeviationsService.GetPlanSummaryMovementIndividuals(request);
      setData(response.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (props.showModal) {
      fetchData();
    }
  }, [
    props.showModal,
    props.scorecardId,
    props.occupationalLevelId,
    props.raceId,
    props.genderId,
    props.movementType,
    pageNumber,
    entriesPerPage,
    searchValue,
    columnSortOptions,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  const entriesPerPageChanged = (size: number) => {
    setEntriesPerPage({ defaultValue: size });
    setPageNumber(1);
  };

  const pageNumberChanged = (page: number) => {
    setPageNumber(page);
  };

  const searchValueChanged = (value: string) => {
    setSearchValue(value);
    setPageNumber(1);
  };

  const columnSortingChanged = (sortOption: ColumnSortOption) => {
    setColumnSortOptions([sortOption]);
    setPageNumber(1);
  };

  const tableData = PlanSummaryMovementIndividualTableMapper(data?.results || [], props.movementType);

  return (
    <Dialog
      open={props.showModal}
      onClose={props.handleCancel}
      maxWidth={false}
      PaperProps={{ sx: { width: 'fit-content', maxWidth: 'calc(100% - 64px)' } }}
    >
      <CustomDialogTitle onClose={props.handleCancel}>{props.title}</CustomDialogTitle>
      <DialogContent sx={{ '& .MuiCard-root': { boxShadow: 'none' } }}>
        <DataTable
          table={tableData}
          canSearch
          entriesPerPage={entriesPerPage}
          pageNumber={pageNumber}
          filteredItemsCount={data?.filteredItemsCount || 0}
          totalItemsCount={data?.totalItemsCount || 0}
          entriesPerPageChanged={entriesPerPageChanged}
          pageNumberChanged={pageNumberChanged}
          searchValueChanged={searchValueChanged}
          isLoading={isLoading}
          manualSorting={true}
          columnSortingChanged={columnSortingChanged}
          showTotalEntries
        />
      </DialogContent>
    </Dialog>
  );
};
